import * as yup from 'yup';
import {
  SUPPORT_CATEGORIES,
  NAME_REG_EXP,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  MESSAGE_MAX_LENGTH,
  MESSAGE_MIN_LENGTH,
  PROFILE_ID_MAX_LENGTH,
  EXCLUDE_CYRILLIC_REG_EXP,
  NUMERIC_ONLY_REG_EXP,
} from './constants';
import { EMAIL_REG_EXP, PHONE_REG_EXP } from '../../shared/constants';

const commonContactUsFields = {
  firstName: yup
    .string()
    .required('First name is required')
    .matches(NAME_REG_EXP, 'Only letters, numbers, and underscore are allowed')
    .min(NAME_MIN_LENGTH, 'First name must be at least 2 characters')
    .max(NAME_MAX_LENGTH, 'First name must be less than 255 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .matches(NAME_REG_EXP, 'Only letters, numbers, and underscore are allowed')
    .min(NAME_MIN_LENGTH, 'Last name must be at least 2 characters')
    .max(NAME_MAX_LENGTH, 'Last name must be less than 255 characters'),
  email: yup
    .string()
    .required('Email is required')
    .matches(EMAIL_REG_EXP, 'Wrong email address format.'),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(
      PHONE_REG_EXP,
      'Please enter at least 10 digits (e.g., +1234567890)',
    ),
  description: yup
    .string()
    .strict(true)
    .required('Brief description is required')
    .matches(EXCLUDE_CYRILLIC_REG_EXP, 'Only latin letters are allowed')
    .test(
      'description-not-numeric-only',
      'Brief description cannot be numbers only',
      value => !value || !NUMERIC_ONLY_REG_EXP.test(value.trim()),
    )
    .min(DESCRIPTION_MIN_LENGTH, 'Description must be at least 2 characters')
    .max(DESCRIPTION_MAX_LENGTH, 'Description must be 150 characters or less'),
  message: yup
    .string()
    .strict(true)
    .required('Message is required')
    .matches(EXCLUDE_CYRILLIC_REG_EXP, 'Only latin letters are allowed')
    .test(
      'message-not-numeric-only',
      'Message cannot be numbers only',
      value => !value || !NUMERIC_ONLY_REG_EXP.test(value.trim()),
    )
    .min(MESSAGE_MIN_LENGTH, 'Message must be at least 2 characters')
    .max(MESSAGE_MAX_LENGTH, 'Message must be 2000 characters or less'),
} as const;

export const contactUsValidationSchema = yup.object({
  ...commonContactUsFields,
  primaryEmail: yup
    .string()
    .required('Primary contact email is required')
    .matches(EMAIL_REG_EXP, 'Wrong email address format.'),
  primaryPhone: yup
    .string()
    .required('Primary contact phone is required')
    .matches(
      PHONE_REG_EXP,
      'Please enter at least 10 digits (e.g., +1234567890)',
    ),
  category: yup
    .string()
    .required('Support category is required')
    .oneOf(SUPPORT_CATEGORIES, 'Please select a valid category'),
  isNotTimeDependent: yup.boolean(),
  startUtc: yup
    .date()
    .typeError('Invalid date')
    .nullable()
    .when('isNotTimeDependent', {
      is: true,
      then: schema => schema.optional(),
      otherwise: schema =>
        schema
          .required('Start time is required')
          .test(
            'start-not-future',
            'Start time cannot be in the future',
            value => !value || value.getTime() <= Date.now(),
          ),
    }),
  endUtc: yup
    .date()
    .typeError('Invalid date')
    .nullable()
    .when('isNotTimeDependent', {
      is: true,
      otherwise: schema =>
        schema
          .test(
            'end-not-future',
            'End time cannot be in the future',
            value => !value || value.getTime() <= Date.now(),
          )
          .min(
            yup.ref('startUtc'),
            'End time must be the same as or after the start time',
          ),
    }),
  dcimDataCenterLocation: yup.string().when('category', {
    is: 'DCIM',
    then: schema =>
      schema
        .required('Data Center Location is required for DCIM')
        .trim()
        .max(NAME_MAX_LENGTH, 'Maximum length is 255 characters'),
    otherwise: schema => schema.optional(),
  }),
  dcimUrl: yup
    .string()
    .trim()
    .when('category', {
      is: 'DCIM',
      then: schema => schema.required('URL is required for DCIM'),
      otherwise: schema => schema.optional(),
    }),
  dcimProfileId: yup.string().when('category', {
    is: 'DCIM',
    then: schema =>
      schema
        .required('Profile ID is required for DCIM')
        .max(PROFILE_ID_MAX_LENGTH, 'Maximum length is 255 characters'),
    otherwise: schema => schema.optional(),
  }),
  dcimDdidSample: yup.string().when('category', {
    is: 'DCIM',
    then: schema =>
      schema
        .required('Data IDs are required for DCIM')
        .max(PROFILE_ID_MAX_LENGTH, 'Maximum length is 255 characters'),
    otherwise: schema => schema.optional(),
  }),
});

export const basicContactUsValidationSchema = yup.object({
  ...commonContactUsFields,
});
